import { getCurrentEnvironment, isRecord } from '@kuow/core';
import type { PaypalKind } from './paypalCheckout';

export interface PaypalActions {
  restart: () => Promise<void>;
}
export interface PaypalButtonOptions {
  fundingSource: 'paypal' | 'venmo';
  style: { layout: 'vertical'; label: 'paypal'; shape: 'rect' };
  createOrder?: () => Promise<string>;
  createSubscription?: () => Promise<string>;
  onClick: (data: { fundingSource?: string }, actions: { resolve: () => Promise<void>; reject: () => Promise<void> }) => Promise<void>;
  onApprove: (data: { orderID?: string; subscriptionID?: string }, actions: PaypalActions) => Promise<void>;
  onCancel: () => void;
  onError: (error: Error) => void;
}
export interface PaypalButton {
  isEligible: () => boolean;
  render: (element: HTMLElement) => Promise<void>;
  close: () => Promise<void>;
}
export interface PaypalSdk {
  Buttons: (options: PaypalButtonOptions) => PaypalButton;
}

const loads = new Map<string, Promise<PaypalSdk>>();

export function loadPaypalSdk(clientId: string, kind: PaypalKind): Promise<PaypalSdk> {
  if (!clientId.trim()) return Promise.reject(new Error('PayPal is unavailable. Please choose another payment method.'));
  const key = `${clientId}:${kind}`;
  const existing = loads.get(key);
  if (existing) return existing;
  // Separate SDK namespaces let monthly and one-time embeds coexist on a page.
  const namespace = `kuowPaypal_${kind}_${Array.from(clientId).map(c => c.charCodeAt(0).toString(16)).join('')}`;
  const query = new URLSearchParams({ 'client-id': clientId, currency: 'USD', components: 'buttons',
    'disable-funding': 'venmo', intent: kind === 'order' ? 'capture' : 'subscription' });
  if (kind === 'subscription') query.set('vault', 'true');
  // Core creates CONTINUE orders; final capture stays on KUOW's submit step.
  if (kind === 'order') query.set('commit', 'false');
  if (getCurrentEnvironment() !== 'production') query.set('buyer-country', 'US');
  const promise = new Promise<PaypalSdk>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = `https://www.paypal.com/sdk/js?${query}`;
    script.dataset.namespace = namespace;
    script.async = true;
    const timer = window.setTimeout(() => fail(), 20_000);
    const fail = () => {
      window.clearTimeout(timer);
      script.remove();
      loads.delete(key);
      reject(new Error('PayPal could not load. Please try again or choose another payment method.'));
    };
    script.onerror = fail;
    script.onload = () => {
      window.clearTimeout(timer);
      const sdk: unknown = Reflect.get(window, namespace);
      if (!isRecord(sdk) || typeof sdk.Buttons !== 'function') { fail(); return; }
      resolve(sdk as unknown as PaypalSdk);
    };
    document.head.append(script);
  });
  loads.set(key, promise);
  return promise;
}
